import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "@legendapp/state/react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { AgentSelector } from "@/components/AgentSelector";
import { CommunicationAgentResultsDialog } from "@/components/communications/CommunicationAgentResultsDialog";
import { AgentClickUpResultsDialog } from "@/components/communications/AgentClickUpResultsDialog";
import { CommunicationInboxToolbar } from "@/components/communications/CommunicationInboxToolbar";
import { CommunicationMessageList } from "@/components/communications/CommunicationMessageList";
import {
  GmailDateFilter,
  type GmailInboxFilterValues,
} from "@/components/communications/GmailInboxFilters";
import { IntegrationRagActionBar } from "@/components/integrations/IntegrationRagActionBar";
import { IntegrationIndexBadge } from "@/components/integrations/IntegrationIndexBadge";
import { useIntegrationIndexMap, getIntegrationIndexStatus } from "@/hooks/useIntegrationIndexMap";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useDebounce } from "@/hooks/use-debounce";
import { useViewPreferences } from "@/hooks/use-view-preferences";
import { useCommunicationAnalyze } from "@/hooks/useCommunicationAnalyze";
import {
  useIntegrationHandoffResume,
} from "@/hooks/useAgentHandoffResume";
import {
  useFirefliesTranscript,
  useFirefliesTranscripts,
  useFormatFirefliesMeeting,
} from "@/hooks/useFireflies";
import { useFirefliesBatchFormat } from "@/hooks/useFirefliesBatchFormat";
import { FirefliesMeetingRow } from "@/components/fireflies/FirefliesMeetingRow";
import { FirefliesBatchResultsDialog } from "@/components/fireflies/FirefliesBatchResultsDialog";
import { WorkflowRunSelector } from "@/components/workflows/WorkflowRunSelector";
import { WorkflowRunDialog } from "@/components/workflows/WorkflowRunDialog";
import { useCommunicationWorkflow } from "@/hooks/useCommunicationWorkflow";
import {
  buildTranscriptBodyForAnalysis,
  formatMeetingDateForAnalysis,
} from "@/lib/fireflies/buildTranscriptBodyForAnalysis";
import { buildFirefliesListParams } from "@/lib/fireflies/buildFirefliesListParams";
import { firefliesToInboxItems } from "@/lib/fireflies/toInboxItem";
import { sortCommunicationItems } from "@/lib/communications/filterSortMessages";
import { firefliesService } from "@/services/firefliesService";
import {
  formatFirefliesMarkdown,
  firefliesMetadata,
} from "@/lib/integrationRag/formatFireflies";
import {
  integrationRagService,
  saveIntegrationLibraryBackup,
} from "@/services/integrationRagService";
import type { AgentAnalysisResult, CommunicationSortField } from "@/types/communications";
import type { FirefliesTranscriptDetail } from "@/types/fireflies";
import {
  firefliesUi$,
  selectFirefliesTranscript,
} from "@/state/firefliesUiLegend";
import type { FirefliesTranscriptSummary } from "@/types/fireflies";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import { AlertCircle, Loader2, Mic, Sparkles } from "lucide-react";

const PAGE_SIZE = 20;

function getVisiblePageNumbers(
  currentPage: number,
  totalPages: number
): (number | "ellipsis")[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages = new Set<number>([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  const result: (number | "ellipsis")[] = [];

  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
      result.push("ellipsis");
    }
    result.push(sorted[i]);
  }

  return result;
}

export default function FirefliesMeetings() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [firefliesFilters, setFirefliesFilters] = useState<GmailInboxFilterValues>({});
  const [batchSelectedIds, setBatchSelectedIds] = useState<Record<string, true>>({});
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [indexing, setIndexing] = useState(false);
  const [indexProgress, setIndexProgress] = useState<string | null>(null);
  const [saveToLibrary, setSaveToLibrary] = useState(false);

  const { viewMode, setViewMode, sortField, setSortField, sortDirection, setSortDirection } =
    useViewPreferences<CommunicationSortField>({
      storageKey: "communications.fireflies",
      defaultViewMode: "grid",
      defaultSortField: "date",
      defaultSortDirection: "desc",
    });

  const selectedId = useSelector(() => firefliesUi$.selectedTranscriptId.get());
  const batchProcessing = useSelector(() => firefliesUi$.batchProcessing.get());

  const analyze = useCommunicationAnalyze();
  useIntegrationHandoffResume(analyze);
  const indexMap = useIntegrationIndexMap("fireflies");
  const workflow = useCommunicationWorkflow("fireflies");

  const listParams = useMemo(
    () =>
      buildFirefliesListParams({
        limit: PAGE_SIZE,
        skip: page * PAGE_SIZE,
        search: debouncedSearch,
        dateFrom: firefliesFilters.dateFrom,
        dateTo: firefliesFilters.dateTo,
      }),
    [page, debouncedSearch, firefliesFilters]
  );

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useFirefliesTranscripts(user?.id, listParams);

  const { data: selectedDetail, isLoading: detailLoading } =
    useFirefliesTranscript(selectedId ?? undefined);

  const formatMutation = useFormatFirefliesMeeting();

  const meetings = data?.transcripts ?? [];
  const sortedMeetings = useMemo(() => {
    const items = firefliesToInboxItems(meetings);
    const sorted = sortCommunicationItems(items, sortField, sortDirection);
    const order = new Map(sorted.map((item, index) => [item.id, index]));
    return [...meetings].sort(
      (a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0)
    );
  }, [meetings, sortField, sortDirection]);

  const sortedInboxItems = useMemo(
    () => sortCommunicationItems(firefliesToInboxItems(sortedMeetings), sortField, sortDirection),
    [sortedMeetings, sortField, sortDirection]
  );

  const meetingsById = useMemo(() => {
    const map = new Map<string, FirefliesTranscriptSummary>();
    for (const m of meetings) map.set(m.id, m);
    if (selectedDetail) map.set(selectedDetail.id, selectedDetail);
    return map;
  }, [meetings, selectedDetail]);

  const { runSequentialFormat } = useFirefliesBatchFormat(
    (id) => formatMutation.mutateAsync(id),
    meetingsById
  );

  const hasNextPage = meetings.length >= PAGE_SIZE;
  const currentPage = page + 1;
  const knownPages = page + 1 + (hasNextPage ? 1 : 0);
  const visiblePages = getVisiblePageNumbers(currentPage, knownPages);
  const hasActiveFilters = Boolean(searchQuery.trim() || firefliesFilters.dateFrom);

  const isFormatBusy = batchProcessing?.active ?? false;
  const isAnalyzing = analyze.isAnalyzing || analyze.clickUpResolving;
  const isAiBusy = isFormatBusy || isAnalyzing || indexing || workflow.isWorkflowRunning;
  const batchSelectedCount = Object.keys(batchSelectedIds).length;

  const allPageSelected =
    sortedMeetings.length > 0 &&
    sortedMeetings.every((m) => batchSelectedIds[m.id]);
  const somePageSelected =
    sortedMeetings.some((m) => batchSelectedIds[m.id]) && !allPageSelected;

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, firefliesFilters.dateFrom, firefliesFilters.dateTo]);

  const handleFirefliesFiltersChange = useCallback((values: GmailInboxFilterValues) => {
    setFirefliesFilters(values);
    setPage(0);
  }, []);

  const handleSortChange = useCallback(
    (field: CommunicationSortField, direction: typeof sortDirection) => {
      setSortField(field);
      setSortDirection(direction);
    },
    [setSortField, setSortDirection]
  );

  const handleSelect = useCallback((id: string) => {
    selectFirefliesTranscript(id);
  }, []);

  const handleBatchToggle = useCallback((id: string) => {
    setBatchSelectedIds((prev) => {
      if (prev[id]) {
        const { [id]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: true };
    });
  }, []);

  const handleToggleAllOnPage = useCallback(
    (checked: boolean) => {
      if (!checked) {
        setBatchSelectedIds((prev) => {
          const next = { ...prev };
          for (const m of sortedMeetings) delete next[m.id];
          return next;
        });
        return;
      }
      setBatchSelectedIds((prev) => {
        const next = { ...prev };
        for (const m of sortedMeetings) next[m.id] = true;
        return next;
      });
    },
    [sortedMeetings]
  );

  const handleFormat = useCallback(
    (id: string) => {
      if (isAiBusy) return;
      selectFirefliesTranscript(id);
      void runSequentialFormat([id]);
    },
    [isAiBusy, runSequentialFormat]
  );

  const handleBatchFormat = useCallback(() => {
    if (isAiBusy || batchSelectedCount === 0) return;
    void runSequentialFormat(Object.keys(batchSelectedIds));
  }, [isAiBusy, batchSelectedCount, batchSelectedIds, runSequentialFormat]);

  const fetchTranscriptDetail = useCallback(
    async (id: string): Promise<FirefliesTranscriptDetail> => {
      if (selectedDetail?.id === id) return selectedDetail;
      return firefliesService.getTranscript(id);
    },
    [selectedDetail]
  );

  const indexTranscripts = useCallback(
    async (
      details: FirefliesTranscriptDetail[],
      contentMode: "raw" | "agent",
      agentContents?: Map<string, string>
    ) => {
      const items = details.map((detail) => {
        const agentBody = agentContents?.get(detail.id);
        const content =
          contentMode === "agent" && agentBody
            ? `# ${detail.title || "Meeting"}\n\n${agentBody}`
            : formatFirefliesMarkdown(detail);
        return {
          source: "fireflies" as const,
          externalId: detail.id,
          title: detail.title || detail.id,
          content,
          metadata: firefliesMetadata(detail),
          remoteUpdatedAt: detail.date ? new Date(detail.date).toISOString() : null,
        };
      });

      const result = await integrationRagService.indexItems(items, {
        contentMode,
        onProgress: (label, completed, total) => {
          setIndexProgress(`Indexing ${completed + 1}/${total}: ${label}`);
        },
      });

      if (saveToLibrary) {
        for (const detail of details) {
          const agentBody = agentContents?.get(detail.id);
          const content =
            contentMode === "agent" && agentBody
              ? `# ${detail.title || "Meeting"}\n\n${agentBody}`
              : formatFirefliesMarkdown(detail);
          await saveIntegrationLibraryBackup({
            source: "fireflies",
            externalId: detail.id,
            title: detail.title || detail.id,
            content,
            metadata: firefliesMetadata(detail),
          });
        }
      }

      await queryClient.invalidateQueries({ queryKey: ["integration-index"] });
      return result;
    },
    [queryClient, saveToLibrary]
  );

  const handleIndexRaw = useCallback(async () => {
    const ids = Object.keys(batchSelectedIds);
    if (ids.length === 0) return;
    setIndexing(true);
    setIndexProgress("Fetching transcripts...");
    try {
      const details: FirefliesTranscriptDetail[] = [];
      for (const id of ids) {
        details.push(await fetchTranscriptDetail(id));
      }
      const result = await indexTranscripts(details, "raw");
      toast({
        title: "Indexed for AI",
        description: `${result.indexed.length} indexed, ${result.failed.length} failed`,
      });
    } catch (e) {
      toast({
        title: "Indexing failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIndexing(false);
      setIndexProgress(null);
    }
  }, [batchSelectedIds, fetchTranscriptDetail, indexTranscripts, toast]);

  const handleIndexAgentResult = useCallback(
    async (result: AgentAnalysisResult) => {
      const summary = meetingsById.get(result.itemId);
      const detail: FirefliesTranscriptDetail = {
        id: result.itemId,
        title: summary?.title || result.itemLabel,
        host_email: summary?.host_email,
        date: summary?.date,
      };
      await indexTranscripts([detail], "agent", new Map([[result.itemId, result.content]]));
      toast({ title: "Indexed agent output for AI" });
    },
    [meetingsById, indexTranscripts, toast]
  );

  const handleIndexAllAgentResults = useCallback(
    async (results: AgentAnalysisResult[]) => {
      const agentContents = new Map(results.map((r) => [r.itemId, r.content]));
      const details = results.map((result) => {
        const summary = meetingsById.get(result.itemId);
        return {
          id: result.itemId,
          title: summary?.title || result.itemLabel,
          host_email: summary?.host_email,
          date: summary?.date,
        } as FirefliesTranscriptDetail;
      });
      const indexResult = await indexTranscripts(details, "agent", agentContents);
      toast({
        title: "Indexed agent results",
        description: `${indexResult.indexed.length} indexed`,
      });
    },
    [meetingsById, indexTranscripts, toast]
  );

  const handleAnalyze = useCallback(async () => {
    if (workflow.selectedWorkflow) {
      const ids = Object.keys(batchSelectedIds);
      await workflow.runWorkflow(async () => {
        const transcripts = [];
        for (const id of ids) {
          const summary = meetingsById.get(id) ?? sortedMeetings.find((m) => m.id === id);
          const label = summary?.title || id;
          try {
            const detail =
              selectedDetail?.id === id
                ? selectedDetail
                : await firefliesService.getTranscript(id);
            transcripts.push({
              id,
              title: detail.title || label,
              host_email: detail.host_email || summary?.host_email,
              date: formatMeetingDateForAnalysis(detail),
              body: buildTranscriptBodyForAnalysis(detail),
            });
          } catch {
            transcripts.push({
              id,
              title: summary?.title || label,
              host_email: summary?.host_email,
              date: summary?.date,
              body:
                summary?.summary?.overview ||
                summary?.summary?.short_summary ||
                summary?.summary?.gist ||
                "",
            });
          }
        }
        return { transcripts, itemIds: ids, subject: transcripts[0]?.title };
      });
      return;
    }
    if (!selectedAgentId || isAiBusy) return;
    const ids = Object.keys(batchSelectedIds);
    const items = [];

    for (const id of ids) {
      const summary = meetingsById.get(id) ?? sortedMeetings.find((m) => m.id === id);
      const label = summary?.title || id;

      try {
        const detail =
          selectedDetail?.id === id
            ? selectedDetail
            : await firefliesService.getTranscript(id);

        items.push({
          id,
          label,
          subject: detail.title || label,
          from: detail.host_email || summary?.host_email || "Unknown host",
          date: formatMeetingDateForAnalysis(detail),
          body: buildTranscriptBodyForAnalysis(detail),
          contentType: "meeting" as const,
        });
      } catch {
        const fallbackBody =
          summary?.summary?.overview ||
          summary?.summary?.short_summary ||
          summary?.summary?.gist ||
          "No transcript content available.";
        items.push({
          id,
          label,
          subject: summary?.title || label,
          from: summary?.host_email || "Unknown host",
          date: summary?.date
            ? formatMeetingDateForAnalysis({ ...summary, date: summary.date })
            : undefined,
          body: fallbackBody,
          contentType: "meeting" as const,
        });
      }
    }

    await analyze.runBatchAnalyze(selectedAgentId, items);
  }, [
    selectedAgentId,
    isAiBusy,
    batchSelectedIds,
    meetingsById,
    sortedMeetings,
    selectedDetail,
    analyze,
    workflow,
  ]);

  const formattingId = batchProcessing?.currentTranscriptId ?? null;
  const emptyMessage = hasActiveFilters
    ? "No meetings match your filters"
    : "No meetings found";

  return (
    <div className="container max-w-6xl py-8 space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Mic className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-semibold">Fireflies Meetings</h1>
          </div>
          <p className="text-muted-foreground mt-1">
            Index meetings for AI chat, analyze with agents, or format for ClickUp
          </p>
        </div>
      </div>

      {isFormatBusy && batchProcessing && (
        <div className="rounded-lg border bg-muted/30 px-4 py-3 flex items-center gap-3 text-sm">
          <Loader2 className="h-4 w-4 animate-spin shrink-0 text-primary" />
          <div className="min-w-0 flex-1">
            <p className="font-medium">
              Formatting {batchProcessing.completed + (batchProcessing.currentTranscriptId ? 1 : 0)} of{" "}
              {batchProcessing.total}
            </p>
            <p className="text-muted-foreground truncate">
              {batchProcessing.currentLabel || "Processing…"}
            </p>
          </div>
        </div>
      )}

      {isAnalyzing && analyze.batchProcessing && (
        <div className="rounded-lg border bg-muted/30 px-4 py-3 flex items-center gap-3 text-sm">
          <Loader2 className="h-4 w-4 animate-spin shrink-0 text-primary" />
          <div className="min-w-0 flex-1">
            <p className="font-medium">
              Analyzing {analyze.batchProcessing.completed + (analyze.batchProcessing.currentItemId ? 1 : 0)} of{" "}
              {analyze.batchProcessing.total}
            </p>
            <p className="text-muted-foreground truncate">
              {analyze.batchProcessing.currentLabel || "Processing…"}
            </p>
          </div>
        </div>
      )}

      {workflow.isWorkflowRunning && workflow.workflowBatchProgress && (
        <div className="rounded-lg border bg-muted/30 px-4 py-3 flex items-center gap-3 text-sm">
          <Loader2 className="h-4 w-4 animate-spin shrink-0 text-primary" />
          <div className="min-w-0 flex-1">
            <p className="font-medium">Running workflow</p>
            <p className="text-muted-foreground truncate">
              {workflow.workflowBatchProgress.currentLabel || "Processing…"}
            </p>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Your meetings</CardTitle>
          <CardDescription>
            {workflow.selectedWorkflow
              ? `Run workflow "${workflow.selectedWorkflow.name}" or analyze meetings with agents.`
              : "Select meetings to index for AI chat, analyze with agents, or batch format for ClickUp."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <CommunicationInboxToolbar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search title, host, or transcript..."
            sortField={sortField}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            disabled={isAiBusy}
            onRefresh={() => void refetch()}
            isRefreshing={isFetching}
            endSlot={
              <GmailDateFilter
                disabled={isAiBusy}
                onChange={handleFirefliesFiltersChange}
              />
            }
          />

          {!isLoading && !isError && sortedMeetings.length > 0 && (
            <div className="space-y-3">
              <div
                className={cn(
                  "space-y-1",
                  workflow.selectedWorkflow && "opacity-60"
                )}
              >
                <span className="text-xs text-muted-foreground">AI Agent</span>
                <AgentSelector
                  value={selectedAgentId}
                  onValueChange={(id) => {
                    setSelectedAgentId(id);
                    if (id) workflow.onWorkflowChange(null, null);
                  }}
                  compact
                  integrationScope="fireflies"
                  disabled={isAiBusy || !!workflow.selectedWorkflow}
                />
                <p className="text-xs text-muted-foreground">
                  Analyze selected meetings — extract insights, deliverables, summaries
                </p>
              </div>
              <div
                className={cn(
                  "space-y-1",
                  selectedAgentId && "opacity-60"
                )}
              >
                <WorkflowRunSelector
                  value={workflow.selectedWorkflowId}
                  integrationSource="fireflies"
                  disabled={isAiBusy || !!selectedAgentId}
                  onChange={(id, wf) => {
                    workflow.onWorkflowChange(id, wf);
                    if (id) setSelectedAgentId(null);
                  }}
                />
              </div>
              <IntegrationRagActionBar
                selectedCount={batchSelectedCount}
                indexing={indexing}
                analyzing={isAnalyzing}
                indexProgress={indexProgress}
                saveToLibrary={saveToLibrary}
                onSaveToLibraryChange={setSaveToLibrary}
                onIndexRaw={() => void handleIndexRaw()}
                onAnalyze={() => void handleAnalyze()}
                disabled={isAiBusy}
              />
              <div className="flex flex-wrap items-center gap-3 rounded-md border bg-muted/20 px-3 py-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="select-all-meetings"
                    checked={allPageSelected ? true : somePageSelected ? "indeterminate" : false}
                    disabled={isAiBusy}
                    onCheckedChange={(checked) =>
                      handleToggleAllOnPage(checked === true)
                    }
                  />
                  <label
                    htmlFor="select-all-meetings"
                    className="text-sm cursor-pointer select-none"
                  >
                    Select all on page
                  </label>
                </div>
                <Button
                  size="sm"
                  disabled={
                    batchSelectedCount === 0 ||
                    isAiBusy ||
                    (!workflow.selectedWorkflow && !selectedAgentId)
                  }
                  onClick={() => void handleAnalyze()}
                >
                  {isAnalyzing || workflow.isWorkflowRunning ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4 mr-2" />
                  )}
                  {workflow.selectedWorkflow ? "Run workflow" : "Analyze selected"}
                  {batchSelectedCount > 0 ? ` (${batchSelectedCount})` : ""}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={batchSelectedCount === 0 || isAiBusy}
                  onClick={handleBatchFormat}
                >
                  {isFormatBusy ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4 mr-2" />
                  )}
                  Format selected{batchSelectedCount > 0 ? ` (${batchSelectedCount})` : ""}
                </Button>
                {batchSelectedCount > 0 && !isAiBusy && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setBatchSelectedIds({})}
                  >
                    Clear selection
                  </Button>
                )}
              </div>
            </div>
          )}

          {isLoading && (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              Loading meetings...
            </div>
          )}

          {isError && (
            <div className="flex items-start gap-3 rounded-lg border border-destructive/50 bg-destructive/5 p-4">
              <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-destructive">Failed to load meetings</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {error instanceof Error ? error.message : "Unknown error"}
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Ensure FIREFLIES_API_KEY is set in your local .env (server) or Supabase Edge secrets (production).
                </p>
              </div>
            </div>
          )}

          {!isLoading && !isError && sortedMeetings.length === 0 && (
            <p className="text-center text-muted-foreground py-12">{emptyMessage}</p>
          )}

          {!isLoading && !isError && sortedMeetings.length > 0 && viewMode === "grid" && (
            <div className="grid gap-3 lg:grid-cols-2">
              {sortedMeetings.map((meeting) => (
                <FirefliesMeetingRow
                  key={meeting.id}
                  meeting={meeting}
                  selected={selectedId === meeting.id}
                  batchChecked={!!batchSelectedIds[meeting.id]}
                  formatting={formattingId === meeting.id}
                  formatBusy={isAiBusy}
                  onSelect={handleSelect}
                  onBatchToggle={handleBatchToggle}
                  onFormat={handleFormat}
                  indexBadge={
                    <IntegrationIndexBadge
                      status={getIntegrationIndexStatus(meeting.id, indexMap)}
                    />
                  }
                />
              ))}
            </div>
          )}

          {!isLoading && !isError && sortedMeetings.length > 0 && viewMode !== "grid" && (
            <CommunicationMessageList
              items={sortedInboxItems}
              viewMode={viewMode}
              selectedIds={batchSelectedIds}
              onToggleSelect={handleBatchToggle}
              onSelectItem={handleSelect}
              activeId={selectedId}
              disabled={isAiBusy}
              emptyMessage={emptyMessage}
              allSelected={allPageSelected}
              someSelected={somePageSelected}
              onSelectAllChange={handleToggleAllOnPage}
              renderTrailingBadge={(item) => (
                <IntegrationIndexBadge
                  status={getIntegrationIndexStatus(item.id, indexMap)}
                />
              )}
            />
          )}

          {(hasNextPage || page > 0) && (
            <Pagination className="pt-2">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setPage((p) => Math.max(0, p - 1));
                    }}
                    className={cn(page === 0 && "pointer-events-none opacity-50")}
                    aria-disabled={page === 0}
                  />
                </PaginationItem>
                {visiblePages.map((pageNum, i) =>
                  pageNum === "ellipsis" ? (
                    <PaginationItem key={`ellipsis-${i}`}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  ) : (
                    <PaginationItem key={pageNum}>
                      <Button
                        variant={pageNum === currentPage ? "outline" : "ghost"}
                        size="icon"
                        className="h-9 w-9"
                        onClick={() => setPage(pageNum - 1)}
                        aria-current={pageNum === currentPage ? "page" : undefined}
                      >
                        {pageNum}
                      </Button>
                    </PaginationItem>
                  )
                )}
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (hasNextPage) setPage((p) => p + 1);
                    }}
                    className={cn(!hasNextPage && "pointer-events-none opacity-50")}
                    aria-disabled={!hasNextPage}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </CardContent>
      </Card>

      {selectedId && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Meeting details</CardTitle>
            {selectedDetail?.title && (
              <CardDescription>{selectedDetail.title}</CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {detailLoading && (
              <div className="flex items-center text-muted-foreground py-4">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Loading transcript...
              </div>
            )}

            {!detailLoading && selectedDetail && (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {selectedDetail.host_email && (
                    <Badge variant="secondary">Host: {selectedDetail.host_email}</Badge>
                  )}
                  {selectedDetail.duration != null && (
                    <Badge variant="outline">
                      {Math.round(selectedDetail.duration)} min
                    </Badge>
                  )}
                </div>

                {selectedDetail.summary?.overview && (
                  <div>
                    <h4 className="text-sm font-medium mb-1">Overview</h4>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                      {selectedDetail.summary.overview}
                    </p>
                  </div>
                )}

                {selectedDetail.sentences && selectedDetail.sentences.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-2">
                      Transcript ({selectedDetail.sentences.length} segments)
                    </h4>
                    <div className="max-h-64 overflow-y-auto rounded-md border p-3 space-y-2 text-sm">
                      {selectedDetail.sentences.slice(0, 80).map((s, i) => (
                        <p key={s.index ?? i}>
                          <span className="font-medium">{s.speaker_name || "Speaker"}:</span>{" "}
                          {s.text}
                        </p>
                      ))}
                      {selectedDetail.sentences.length > 80 && (
                        <p className="text-muted-foreground italic">
                          … {selectedDetail.sentences.length - 80} more segments
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <Button onClick={() => handleFormat(selectedId)} disabled={isAiBusy}>
                  {formattingId === selectedId ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Sparkles className="h-4 w-4 mr-2" />
                  )}
                  {formattingId === selectedId ? "Formatting…" : "Format with AI"}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <FirefliesBatchResultsDialog />

      <CommunicationAgentResultsDialog
        open={analyze.resultsOpen && analyze.lastActionType === "none"}
        onOpenChange={analyze.setResultsOpen}
        results={analyze.results}
        agent={analyze.lastAgent}
        handoffSource="fireflies"
        returnPath="/fireflies"
        items={analyze.lastItems}
        actionType={analyze.lastActionType}
        onIndexResult={handleIndexAgentResult}
        onIndexAll={handleIndexAllAgentResults}
      />

      <AgentClickUpResultsDialog
        open={analyze.clickUpActionOpen}
        onOpenChange={analyze.setClickUpActionOpen}
        agent={analyze.clickUpActionContext?.agent ?? null}
        teamId={analyze.clickUpActionContext?.teamId ?? ""}
        meetings={analyze.clickUpActionContext?.meetings ?? []}
        handoffSource="fireflies"
        returnPath="/fireflies"
        items={analyze.lastItems}
        results={analyze.results}
        onIndexForRag={
          analyze.results.length > 0
            ? () => handleIndexAllAgentResults(analyze.results)
            : undefined
        }
      />

      <WorkflowRunDialog
        open={workflow.inspectorOpen}
        onOpenChange={workflow.setInspectorOpen}
        runId={workflow.activeRunId}
        workflow={workflow.selectedWorkflow}
      />
    </div>
  );
}
